import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { getRecipes, getSubscription, getMe, getSemifinished } from '../api/client'
import { BookOpen, FileText, ClipboardCheck, Plus, ChevronRight, Clock, Star, Lock, Zap, Package, AlertTriangle } from 'lucide-react'

function daysRemaining(expiryDate: string) {
  const diff = new Date(expiryDate).setHours(0,0,0,0) - new Date().setHours(0,0,0,0)
  return Math.floor(diff / 86400000)
}

export default function DashboardPage() {
  const navigate = useNavigate()

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: () => getMe().then(r => r.data) })
  const { data: recipes = [] } = useQuery({ queryKey: ['recipes'], queryFn: () => getRecipes().then(r => r.data) })
  const { data: sub } = useQuery({ queryKey: ['subscription'], queryFn: () => getSubscription().then(r => r.data) })
  const { data: semifinished = [] } = useQuery({ queryKey: ['semifinished'], queryFn: () => getSemifinished(true).then(r => r.data) })

  const count = (recipes as any[]).length
  const expiringItems = (semifinished as any[]).filter(i => daysRemaining(i.expiry_date) <= 2)
  const expiredItems = expiringItems.filter(i => daysRemaining(i.expiry_date) < 0)
  const recent = (recipes as any[]).slice(-3).reverse()
  const planLabel = sub?.plan === 'free' ? 'Ingyenes' : sub?.plan === 'starter' ? 'Alap' : 'Pro'
  const atLimit = sub && !sub.can_create

  return (
    <div className="max-w-4xl">
      {/* Üdvözlő fejléc */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Üdvözöljük{me?.name ? `, ${me.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Receptjeiből automatikusan generál gyártmánylapot, HACCP dokumentációt és etikett-címkéket.
        </p>
      </div>

      {/* Státusz sor */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="card p-4">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-brand-600" />
            </div>
            <span className="text-xs font-medium text-gray-500">Receptek</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{count}</div>
          {sub?.recipe_limit && (
            <div className="text-xs text-gray-400 mt-0.5">/ {sub.recipe_limit} max</div>
          )}
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-gray-500">Gyártmánylapok</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{count}</div>
          <div className="text-xs text-gray-400 mt-0.5">PDF generálható</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <ClipboardCheck className="w-4 h-4 text-red-500" />
            </div>
            <span className="text-xs font-medium text-gray-500">HACCP dok.</span>
          </div>
          <div className="text-2xl font-bold text-gray-900">{count}</div>
          <div className="text-xs text-gray-400 mt-0.5">HACCP + Származási</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Star className="w-4 h-4 text-amber-500" />
            </div>
            <span className="text-xs font-medium text-gray-500">Csomag</span>
          </div>
          <div className="text-lg font-bold text-gray-900">{planLabel}</div>
          {sub?.bekas_pack && (
            <div className="text-xs text-amber-600 font-medium mt-0.5">+ Békás Prémium</div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Gyors műveletek */}
        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 mb-3 text-sm">Gyors műveletek</h2>
          <div className="space-y-2">
            <button
              onClick={() => {
                if (atLimit) return
                navigate('/receptek')
              }}
              disabled={atLimit}
              className={`w-full flex items-center gap-3 p-3 rounded-xl text-sm text-left transition-colors ${
                atLimit
                  ? 'opacity-50 cursor-not-allowed bg-gray-50'
                  : 'bg-brand-50 hover:bg-brand-100 text-brand-700'
              }`}
            >
              {atLimit ? <Lock className="w-4 h-4 shrink-0" /> : <Plus className="w-4 h-4 shrink-0" />}
              <span className="font-medium">Új recept felvitele</span>
            </button>
            <button
              onClick={() => navigate('/receptek')}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-sm text-left bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors"
            >
              <FileText className="w-4 h-4 shrink-0 text-gray-400" />
              <span>Gyártmánylap generálás</span>
            </button>
            <button
              onClick={() => navigate('/haccp')}
              className="w-full flex items-center gap-3 p-3 rounded-xl text-sm text-left bg-gray-50 hover:bg-gray-100 text-gray-700 transition-colors"
            >
              <ClipboardCheck className="w-4 h-4 shrink-0 text-gray-400" />
              <span>HACCP napló rögzítése</span>
            </button>
          </div>
        </div>

        {/* Legutóbbi receptek */}
        <div className="card p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-900 text-sm">Legutóbbi receptek</h2>
            <button onClick={() => navigate('/receptek')}
              className="text-xs text-brand-600 hover:text-brand-700 font-medium flex items-center gap-1">
              Mind <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
          {recent.length === 0 ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-400 mb-3">Még nincs recept</p>
              <button onClick={() => navigate('/receptek')}
                className="btn-primary text-sm">Első recept hozzáadása</button>
            </div>
          ) : (
            <div className="space-y-2">
              {recent.map((r: any) => (
                <button key={r.id}
                  onClick={() => navigate('/receptek')}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-50 hover:bg-gray-100 text-left transition-colors">
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{r.name}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-2 mt-0.5">
                      <span>{r.ingredients?.length ?? 0} alapanyag</span>
                      {r.shelf_life_days && (
                        <span className="flex items-center gap-0.5 text-brand-600">
                          <Clock className="w-3 h-3" />{r.shelf_life_days} nap
                        </span>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Limit / upsell banner */}
      {atLimit && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-semibold text-amber-900 text-sm">Elérte a receptek korlátját</div>
            <p className="text-xs text-amber-700 mt-0.5">
              {planLabel} csomag: {sub.recipe_count} / {sub.recipe_limit} recept. Váltson magasabb csomagra.
            </p>
          </div>
          <button onClick={() => navigate('/beallitasok?tab=előfizetés')}
            className="shrink-0 flex items-center gap-1.5 bg-amber-400 hover:bg-amber-500 text-white text-xs font-semibold px-4 py-2 rounded-lg transition-colors">
            <Zap className="w-3.5 h-3.5" /> Bővítés
          </button>
        </div>
      )}

      {/* Félkész termék lejárati figyelmeztetés */}
      {expiringItems.length > 0 && (
        <div className={`mt-4 rounded-xl border p-4 flex flex-wrap items-center justify-between gap-3 ${
          expiredItems.length > 0 ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
        }`}>
          <div className="flex items-center gap-3">
            <Package className={`w-5 h-5 shrink-0 ${expiredItems.length > 0 ? 'text-red-500' : 'text-amber-500'}`} />
            <div>
              <div className={`font-semibold text-sm ${expiredItems.length > 0 ? 'text-red-900' : 'text-amber-900'}`}>
                {expiredItems.length > 0
                  ? `${expiredItems.length} félkész termék lejárt!`
                  : `${expiringItems.length} félkész termék hamarosan lejár`
                }
              </div>
              <p className={`text-xs mt-0.5 ${expiredItems.length > 0 ? 'text-red-700' : 'text-amber-700'}`}>
                {expiringItems.map((i: any) => {
                  const d = daysRemaining(i.expiry_date)
                  return d < 0 ? `${i.name} (lejárt)` : d === 0 ? `${i.name} (ma)` : `${i.name} (${d} nap)`
                }).join(' · ')}
              </p>
            </div>
          </div>
          <button onClick={() => navigate('/haccp')}
            className={`shrink-0 text-xs font-semibold flex items-center gap-1 px-3 py-2 rounded-lg transition-colors ${
              expiredItems.length > 0
                ? 'bg-red-100 hover:bg-red-200 text-red-700'
                : 'bg-amber-100 hover:bg-amber-200 text-amber-700'
            }`}>
            <AlertTriangle className="w-3.5 h-3.5" /> Megnézem
          </button>
        </div>
      )}

      {/* Békás upsell ha nincs */}
      {!sub?.bekas_pack && (
        <div className="mt-4 rounded-xl border border-dashed border-amber-300 bg-amber-50/50 p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Star className="w-5 h-5 text-amber-400 shrink-0" />
            <div>
              <div className="font-medium text-gray-800 text-sm">Békás Kft. Prémium — 182 cukrászati alapanyag</div>
              <p className="text-xs text-gray-500">Tápérték, allergén, összetétel adatok — azonnal receptekbe rakható</p>
            </div>
          </div>
          <button onClick={() => navigate('/alapanyagok')}
            className="shrink-0 text-xs font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1">
            Megnézem <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
    </div>
  )
}
